// ─── La biblioteca de animaciones gráficas del proyecto ──────────────────────
// Cada animación generada (desde el panel "Animación gráfica" o por el agente)
// queda guardada acá, aparte del clip, para verla en Archivos → Animaciones y
// volver a ponerla en la timeline. Vive en `video_projects.state` con los clips.
// Puro: el editor la escribe con `setAnimacionesGraficas`.

import type { AnimacionPorCodigo } from './versiones';

export type AnimacionGraficaGuardada = {
  id: string;
  nombre: string;
  animacion: AnimacionPorCodigo;
  /** Captura del medio de la animación, JPEG chico como data URL. */
  miniatura?: string;
  /** ISO. */
  creadaEn: string;
};

/** Tope de animaciones guardadas por proyecto: al pasar, se va la más vieja. */
export const MAX_ANIMACIONES_GUARDADAS = 60;

/** Ancho de la miniatura, en px: un JPEG de 320 pesa ~20 KB en el estado. */
export const ANCHO_MINIATURA = 320;

export function guardarAnimacion(
  lista: AnimacionGraficaGuardada[],
  nueva: AnimacionGraficaGuardada,
): AnimacionGraficaGuardada[] {
  const sinRepetir = lista.filter((a) => a.id !== nueva.id);
  const conNueva = [nueva, ...sinRepetir];
  return conNueva.slice(0, MAX_ANIMACIONES_GUARDADAS);
}

/** Actualiza una guardada (por ejemplo, la animación tras un cambio del agente, o
 *  la miniatura). Si no está, deja la lista como está. */
export function actualizarAnimacion(
  lista: AnimacionGraficaGuardada[],
  id: string,
  patch: Partial<Omit<AnimacionGraficaGuardada, 'id'>>,
): AnimacionGraficaGuardada[] {
  if (!lista.some((a) => a.id === id)) return lista;
  return lista.map((a) => (a.id === id ? { ...a, ...patch } : a));
}

export function quitarAnimacion(
  lista: AnimacionGraficaGuardada[],
  id: string,
): AnimacionGraficaGuardada[] {
  return lista.filter((a) => a.id !== id);
}

/** ¿Es una lista guardada válida? Para hidratar desde el estado sin confiar en él. */
export function esListaDeAnimacionesGuardadas(v: unknown): v is AnimacionGraficaGuardada[] {
  return (
    Array.isArray(v) &&
    v.every(
      (a) =>
        typeof a === 'object' &&
        a !== null &&
        typeof (a as AnimacionGraficaGuardada).id === 'string' &&
        typeof (a as AnimacionGraficaGuardada).nombre === 'string' &&
        typeof (a as AnimacionGraficaGuardada).animacion === 'object' &&
        Array.isArray((a as AnimacionGraficaGuardada).animacion?.versiones),
    )
  );
}

/** Un nombre corto para la timeline y la biblioteca a partir del resumen o del prompt. */
export function nombreDeAnimacion(resumen: string, prompt: string): string {
  const base = (resumen || prompt).trim().replace(/\s+/g, ' ');
  if (!base) return 'Animación gráfica';
  return base.length > 40 ? `${base.slice(0, 37)}…` : base;
}
